import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "../../../services/axios/backendAxios";
import HeaderBar from "../../../components/Header/HeaderBar";
import UserPermissions, {
  gatherPermissions,
} from "../../../components/UserPermissions";
import ContentWrapper from "../../../components/ContentWrapper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesContaining } from "../../../utils/queryInvalidation";
import flash from "../../../services/flash";
import { ObjectId } from "bson";
import Select from "react-select";
import AuthLabeledTextInput from "../../../components/Inputs/AuthLabeledTextInput";
import CheckBox from "../../../components/Inputs/CheckBox";
import { useRef } from "react";
import PhoneNumberInput, {
  normalizePhoneNumberValue,
} from "../../../components/Inputs/PhoneNumberInput";
import { confirmWrapper } from "../../../components/Modals/ConfirmWrapper";
import { useTranslation } from "react-i18next";

const AdminEmployeeDetailsPage = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let { id } = useParams();
  const [searchParams] = useSearchParams();

  const makeNew = id === "new";

  const EmployeeTypeOptions = [
    { value: true, label: t("employeeDetails.typeMobile") },
    { value: false, label: t("employeeDetails.typeTimeClock") },
  ];

  const [employee, setEmployee] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [employeeLoading, setEmployeeLoading] = useState(true);

  const isSelf = props.currentUser.uid == employee.uid;

  const queryClient = useQueryClient();
  const companyQuery = useQuery({
    queryKey: [`company/${props.currentUser.currentCompanyId}`],
  });
  const usageQuery = useQuery({
    queryKey: [`company/${props.currentUser.currentCompanyId}/usage`],
    enabled: makeNew,
  });
  const saveEmployeeMutation = useMutation({
    mutationFn: (employeeData) => {
      return axios.post(`user/update-or-create/${employeeData._id}`, {
        ...employeeData,
        companyId: props.currentUser.currentCompanyId,
      });
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? t("employeeDetails.userSaved"));
    },
    onSettled: () => {
      invalidateQueriesContaining(queryClient, [
        "company",
        "companies",
        "user",
      ]);
    },
    onError: (err) => {
      let details = err.response?.data;
      console.log(details);
      flash.error(
        t("employeeDetails.errorSaving", {
          message:
            details?.message ?? details ?? t("employeeDetails.unknownError"),
        }),
      );
    },
  });
  const removeFromCompanyMutation = useMutation({
    mutationFn: (user) => {
      return axios.post(`user/${user._id}/remove-from-company`, {
        companyId: props.currentUser.currentCompanyId,
      });
    },
    onSuccess: () => {
      flash.success(t("employeeDetails.userRemoved"));
      navigate("/admin/employee");
    },
    onSettled: () => {
      invalidateQueriesContaining(queryClient, [
        "company",
        "companies",
        "user",
      ]);
    },
    onError: (err) => {
      let details = err.response?.data;
      console.log(details);
      flash.error(
        t("employeeDetails.errorRemovingUser", {
          message: details?.message ?? details,
        }),
      );
    },
  });

  const loading =
    employeeLoading || companyQuery.isLoading || usageQuery.isLoading;

  const formRef = useRef(null);
  useEffect(() => {
    if (makeNew) {
      setEmployee({
        _id: new ObjectId().toString(),
        active: searchParams.get("type") === "timeclock" ? false : true,
      });
      setEmployeeLoading(false);
    } else {
      setEmployeeLoading(true);
      axios
        .get(`user/by-id/${id}?companyId=${props.currentUser.currentCompanyId}`)
        .then((res) => setEmployee(res.data))
        .finally(() => setEmployeeLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, makeNew, props.currentUser.currentCompanyId]);

  const employeeActive =
    companyQuery.data?.authorizedUsers[id]?.active ?? false;

  let title = t("employeeDetails.title");
  if (makeNew) {
    title = t("employeeDetails.newTitle");
  } else if (employee.firstName && employee.lastName) {
    title = `${employee.firstName} ${employee.lastName}`;
  }
  const onSubmit = useCallback(
    async (e) => {
      console.log("calling on submit");
      try {
        setIsSaving(true);
        const form = e.nativeEvent.target;
        let newData = {
          _id: form.id?.value,
          firstName: form.first_name?.value,
          lastName: form.last_name?.value,
          phoneNumber: normalizePhoneNumberValue(form.phone?.value),
          active: makeNew ? true : form.active?.checked,
          permissions: makeNew ? ["view"] : gatherPermissions(form),
        };
        console.log(newData);
        if (
          !makeNew &&
          employee.phoneNumber &&
          newData.phoneNumber &&
          employee.phoneNumber !== newData.phoneNumber
        ) {
          const confirmed = await confirmWrapper(
            t("employeeDetails.changePhoneTitle"),
            {
              okText: t("employeeDetails.updatePhoneNumber"),
              description: t("employeeDetails.changePhoneDescription", {
                firstName: employee.firstName,
                phone: newData.phoneNumber,
              }),
            },
          );
          if (!confirmed) {
            return;
          }
        }
        try {
          const resp = await saveEmployeeMutation.mutateAsync(newData).catch();
          if (makeNew) {
            navigate(`/admin/employee/${resp.data.user._id}/instructions`);
          } else {
            navigate(`/admin/employee`);
          }
        } catch (err) {
          // pass - handled in mutation onError
        }
      } finally {
        setIsSaving(false);
      }
    },
    [employee, makeNew, navigate, saveEmployeeMutation],
  );
  if (
    makeNew &&
    usageQuery.isSuccess &&
    usageQuery.data.activeUsers >= usageQuery.data.authorizedUsers
  ) {
    return (
      <>
        <p>{t("employeeDetails.upgradeToAdd")}</p>
      </>
    );
  }

  let buttons = [];

  console.log(props.currentUser);
  console.log(employee._id);
  if (!makeNew && !isSelf) {
    buttons.push({
      color: "red",
      label: t("common.remove"),
      onClick: async () => {
        if (
          await confirmWrapper(t("employeeDetails.removeTitle"), {
            okText: t("common.remove"),
            description: t("employeeDetails.removeDescription"),
          })
        ) {
          removeFromCompanyMutation.mutate(employee);
        }
      },
    });
  }
  buttons.push({
    color: "green",
    label: t("common.save"),
    loading: isSaving,
    onClick: async () => {
      formRef.current.requestSubmit();
    },
  });

  return (
    <>
      <HeaderBar buttons={buttons} title={title} />
      <ContentWrapper loading={loading}>
        {loading ? null : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(e);
            }}
            ref={formRef}
          >
            <input type="hidden" name="id" value={employee._id} />
            <input type="submit" hidden />
            {/* this allows enter to submit the form */}
            <AuthLabeledTextInput
              label={t("employeeDetails.firstName")}
              defaultValue={employee.firstName}
              name="first_name"
              required={true}
            />
            <AuthLabeledTextInput
              label={t("employeeDetails.lastName")}
              defaultValue={employee.lastName ?? ""}
              name="last_name"
              required={true}
            />
            <PhoneNumberInput
              key={
                employee._id
                  ? employee._id + (employee.phoneNumber || "")
                  : "new"
              }
              required={true}
              disabled={
                !(makeNew || employee.phoneChangeEligibility?.canChange)
              }
              defaultValue={employee.phoneNumber ?? ""}
            />
            {!makeNew && !employee.phoneChangeEligibility?.canChange && (
              <p className="text-xs text-gray-500 mt-1 mb-2">
                {employee.phoneChangeEligibility?.reason ||
                  t("employeeDetails.phoneChangeNotAllowed")}
              </p>
            )}
            {makeNew ? (
              <>
                <div className={`text-sm font-bold mb-0.5 text-snow-primary`}>
                  {t("employeeDetails.employeeType")}
                </div>
                <Select
                  name="active"
                  onChange={(val) =>
                    setEmployee({ ...employee, active: val.value })
                  }
                  options={EmployeeTypeOptions}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: "#829399",
                    }),
                  }}
                  value={
                    employee.active === false
                      ? EmployeeTypeOptions.find((o) => o.value === false)
                      : EmployeeTypeOptions.find((o) => o.value === true)
                  }
                />
                <p className="text-xs text-snow-primary font-light mt-1">
                  {employee.active
                    ? t("employeeDetails.normalEmployeeDescription")
                    : t("employeeDetails.timeClockDescription")}
                </p>
              </>
            ) : (
              <>
                <CheckBox
                  name="active"
                  defaultChecked={employeeActive}
                  label={t("employeeDetails.active")}
                  disabled={isSelf}
                  description={t("employeeDetails.activeDescription")}
                />
                {!makeNew ? (
                  <UserPermissions
                    userToEdit={employee}
                    companyId={props.currentUser.currentCompanyId}
                  />
                ) : null}
              </>
            )}
          </form>
        )}
      </ContentWrapper>
    </>
  );
};
const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminEmployeeDetailsPage);
export { AdminEmployeeDetailsPage };
